import { kebabCaseJoin } from '../utils';
import { DESCOPE_PREFIX } from '../constants';

export const observeAttributes = (
  ele,
  callback,
  { excludeAttrs = [], includeAttrs = [] },
) => {
  // sync all attrs on init
  const filteredAttrs = Array.from(ele.attributes)
    .filter(
      (attr) =>
        !excludeAttrs.includes(attr.name) &&
        (!includeAttrs.length || includeAttrs.includes(attr.name)),
    )
    .map((attr) => attr.name);

  callback(filteredAttrs);

  const observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        !excludeAttrs.includes(mutation.attributeName) &&
        (!includeAttrs.length || includeAttrs.includes(mutation.attributeName))
      ) {
        callback([mutation.attributeName]);
      }
    });
  });

  observer.observe(ele, { attributes: true });

  return { disconnect: () => observer.disconnect() };
};

// calling the callback with this object: { addedNodes, removedNodes }
export const observeChildren = (ele, callback) => {
  callback({ addedNodes: Array.from(ele.children), removedNodes: [] });

  const observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'childList' || mutation.type === 'characterData') {
        callback(mutation);
      }
    });
  });

  observer.observe(ele, {
    childList: true,
    characterData: true,
    subtree: true,
  });
};

const createSyncAttrsCb =
  (srcEle, targetEle, mapAttrs = {}) =>
  (attrNames) => {
    attrNames.forEach((attrName) => {
      const targetAttrName = mapAttrs[attrName] || attrName;
      const srcAttrVal = srcEle.getAttribute(attrName);

      if (!targetEle) return;

      if (srcAttrVal !== null) {
        if (targetEle.getAttribute(targetAttrName) !== srcAttrVal) {
          targetEle.setAttribute(targetAttrName, srcAttrVal);
        }
      } else {
        targetEle.removeAttribute(targetAttrName);
      }
    });
  };

export const syncAttrs = (ele1, ele2, options) => {
  observeAttributes(ele1, createSyncAttrsCb(ele1, ele2), options);
  observeAttributes(ele2, createSyncAttrsCb(ele2, ele1), options);
};

export const getComponentName = (name) => kebabCaseJoin(DESCOPE_PREFIX, name);

export const getCssVarName = (...args) => `--${kebabCaseJoin(...args)}`;

export const forwardAttrs = (source, dest, options = {}) =>
  observeAttributes(
    source,
    createSyncAttrsCb(source, dest, options.mapAttrs),
    options,
  );

export const forwardProps = (src, target, props = []) => {
  if (!props.length) return;

  const config = props.reduce(
    (acc, prop) =>
      Object.assign(acc, {
        [prop]: {
          get() {
            return src[prop];
          },
          set(v) {
            src[prop] = v;
          },
        },
      }),
    {},
  );

  Object.defineProperties(target, config);
};

export const injectStyle = (cssString, ref, { prepend = false } = {}) => {
  let style;

  try {
    style = new CSSStyleSheet();
  } catch {
    // fallback for browsers that don't support CSSStyleSheet
    return generateStyleTagFallback(cssString, ref, { prepend });
  }

  const _ref = ref?.shadowRoot || ref;
  if (cssString) {
    style.replaceSync(cssString);
  }
  if (_ref) {
    const adoptedStyleSheets = [...(_ref.adoptedStyleSheets || [])];
    adoptedStyleSheets[prepend ? 'unshift' : 'push'](style);

    _ref.adoptedStyleSheets = adoptedStyleSheets;
  }

  if (cssString && !style.cssRules[0]?.style) {
    console.warn(
      `No style rules were created, make sure the CSS is valid:\n "${cssString}"`,
    );
  }

  return style;
};

// we should mimic the CSSStyleSheet API for the fns we are using
class CSSStyleSheetMock {
  constructor(cssString, ref, { prepend = false } = {}) {
    this.styleEle = document.createElement('style');
    this.styleEle.textContent = cssString;
    this.styleEle.setAttribute('nonce', window.DESCOPE_NONCE);
    this.ref = ref?.shadowRoot || ref;

    if (!this.ref) {
      return;
    }

    if (prepend) {
      this.ref.prepend(this.styleEle);
    } else {
      this.ref.append(this.styleEle);
    }
  }

  replaceSync(cssString) {
    this.styleEle.textContent = cssString;
  }

  get cssRules() {
    return this.styleEle.sheet?.cssRules;
  }
}

const generateStyleTagFallback = (cssString, ref, { prepend = false } = {}) =>
  new CSSStyleSheetMock(cssString, ref, { prepend });

export const limitAbbreviation = (str, limit = 2) =>
  str
    .trim()
    .split(' ')
    .splice(0, limit)
    .map((s) => s[0]?.toUpperCase())
    .join('');

export const cloneSlottedNodes = (element, slotName) => {
  if (!element || !slotName) return null;
  const sources = element.querySelectorAll(`:scope > [slot="${slotName}"]`);
  if (!sources.length) return null;
  const fragment = document.createDocumentFragment();
  sources.forEach((source) => {
    const clone = source.content
      ? source.content.cloneNode(true)
      : source.cloneNode(true);
    if (clone.removeAttribute) clone.removeAttribute('slot');
    fragment.append(clone);
  });
  return fragment;
};
